import { useRouter } from 'expo-router';
import { Platform, Pressable, StyleSheet, View } from 'react-native';
import { useTranslation } from '../../contexts/TranslationContext';
import { trackEvent } from '../../lib/analytics';
import { useMixpanel } from '../../lib/mixpanel';
import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';
import { Container } from '../primitives/Container';
import { Text } from '../primitives/Text';
import { scrollToSection } from './scrollToSection';

type ScrollTarget = 'features' | 'pricing' | 'integrations';

type FooterLinkKey =
  | 'features'
  | 'pricing'
  | 'integrations'
  | 'changelog'
  | 'blog'
  | 'about'
  | 'press'
  | 'privacy'
  | 'terms'
  | 'cookie'
  | 'gdpr';

interface FooterLink {
  label: string;
  linkKey: FooterLinkKey;
  href?: string;
  scrollTarget?: ScrollTarget;
}

export const PremiumFooter = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useTranslation();
  const router = useRouter();
  const mp = useMixpanel();

  const columns: { title: string; links: FooterLink[] }[] = [
    {
      title: t('landing.footer.columns.product'),
      links: [
        {
          label: t('landing.footer.links.features'),
          linkKey: 'features',
          scrollTarget: 'features',
        },
        {
          label: t('landing.footer.links.pricing'),
          linkKey: 'pricing',
          scrollTarget: 'pricing',
        },
        {
          label: t('landing.footer.links.integrations'),
          linkKey: 'integrations',
          scrollTarget: 'integrations',
        },
        {
          label: t('landing.footer.links.changelog'),
          linkKey: 'changelog',
          href: '/changelog',
        },
        {
          label: t('landing.footer.links.blog'),
          linkKey: 'blog',
          href: '/blog',
        },
      ],
    },
    {
      title: t('landing.footer.columns.company'),
      links: [
        { label: t('landing.footer.links.about'), linkKey: 'about', href: '/about' },
        { label: t('landing.footer.links.press'), linkKey: 'press', href: '/press' },
      ],
    },
    {
      title: t('landing.footer.columns.legal'),
      links: [
        {
          label: t('landing.footer.links.privacy'),
          linkKey: 'privacy',
          href: '/privacy-policy',
        },
        { label: t('landing.footer.links.terms'), linkKey: 'terms', href: '/terms' },
        {
          label: t('landing.footer.links.cookie'),
          linkKey: 'cookie',
          href: '/cookie-policy',
        },
        { label: t('landing.footer.links.gdpr'), linkKey: 'gdpr', href: '/gdpr' },
      ],
    },
  ];

  const socialLinks = [
    t('landing.footer.social.twitter'),
    t('landing.footer.social.linkedin'),
    t('landing.footer.social.github'),
  ];

  const handleNavLink = (link: FooterLink) => {
    trackEvent(mp, 'landing.footer_link_clicked', {
      link: link.linkKey,
      type: 'nav',
    });
    router.push(link.href as '/');
  };

  const handleScrollLink = (link: FooterLink) => {
    const target = link.scrollTarget as ScrollTarget;
    trackEvent(mp, 'landing.footer_link_clicked', {
      link: link.linkKey,
      type: 'scroll',
    });
    scrollToSection(target);
  };

  return (
    <View style={styles.footer}>
      <View style={styles.topBorder} />

      <Container>
        <View style={styles.content}>
          <View style={styles.brand}>
            <Text variant="button" style={styles.brandText}>
              Flow
              <Text variant="button" color={colors.primary[400]} style={styles.brandText}>
                SellAI
              </Text>
            </Text>
            <Text variant="bodySmall" color={colors.text.secondary} style={styles.tagline}>
              {t('landing.footer.tagline')}
            </Text>
          </View>

          <View style={styles.links}>
            {columns.map((column) => (
              <View key={column.title} style={styles.linkColumn}>
                <Text variant="overline" color={colors.text.tertiary} style={styles.columnTitle}>
                  {column.title}
                </Text>
                {column.links.map((link) => {
                  if (link.scrollTarget) {
                    return (
                      <Pressable
                        key={link.linkKey}
                        testID={`footer-link-${link.linkKey}`}
                        onPress={() => handleScrollLink(link)}
                        accessibilityRole="link"
                      >
                        <Text variant="bodySmall" color={colors.text.secondary} style={styles.link}>
                          {link.label}
                        </Text>
                      </Pressable>
                    );
                  }
                  return (
                    <Pressable
                      key={link.linkKey}
                      testID={`footer-link-${link.linkKey}`}
                      onPress={() => handleNavLink(link)}
                      accessibilityRole="link"
                    >
                      <Text variant="bodySmall" color={colors.text.secondary} style={styles.link}>
                        {link.label}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>
            ))}
          </View>
        </View>

        <View style={styles.bottom}>
          <Text variant="caption" color={colors.text.tertiary}>
            © {currentYear} {t('landing.footer.brand')}. {t('landing.footer.copyright')}
          </Text>
          <View style={styles.social}>
            {socialLinks.map((link, index) => (
              <Text key={link} variant="caption" color={colors.text.tertiary}>
                {index === 0 ? link : `•  ${link}`}
              </Text>
            ))}
          </View>
        </View>
      </Container>
    </View>
  );
};

const styles = StyleSheet.create({
  footer: {
    backgroundColor: colors.background.primary,
    paddingTop: spacing.xl,
    paddingBottom: spacing.xxl,
  },
  topBorder: {
    height: 1,
    backgroundColor: colors.border.muted,
    marginBottom: spacing.xxl,
  },
  content: {
    flexDirection: Platform.select({ web: 'row', native: 'column' }),
    justifyContent: 'space-between',
    alignItems: Platform.select({ web: 'flex-start', native: 'stretch' }),
    gap: spacing.xxl,
    marginBottom: spacing.xxl,
    paddingVertical: spacing.lg,
  },
  brand: {
    flex: Platform.select({ web: 1, native: undefined }),
    maxWidth: Platform.select({ web: 280, native: undefined }),
  },
  brandText: {
    marginBottom: spacing.sm,
    fontSize: 18,
    fontWeight: '700',
    letterSpacing: -0.3,
    color: colors.text.primary,
  },
  tagline: {
    lineHeight: 22,
    maxWidth: 240,
  },
  links: {
    flexDirection: 'row',
    gap: Platform.select({ web: spacing.xxl, native: spacing.lg }),
    flexWrap: 'wrap',
    flex: Platform.select({ web: 2, native: undefined }),
    justifyContent: Platform.select({ web: 'flex-end', native: 'flex-start' }),
  },
  linkColumn: {
    minWidth: Platform.OS === 'web' ? 130 : '45%',
    marginBottom: spacing.md,
    gap: spacing.sm,
  },
  columnTitle: {
    marginBottom: spacing.xs,
  },
  link: {
    marginBottom: spacing.xs,
  },
  bottom: {
    flexDirection: Platform.select({ web: 'row', native: 'column' }),
    justifyContent: 'space-between',
    alignItems: Platform.select({ web: 'center', native: 'flex-start' }),
    gap: spacing.md,
    paddingTop: spacing.xl,
    borderTopWidth: 1,
    borderTopColor: colors.border.subtle,
  },
  social: {
    flexDirection: 'row',
    gap: spacing.md,
    alignItems: 'center',
  },
});
